export interface BillingProps {
  loading: boolean;
}

/** Usage data for the current billing period */
export interface UsageData {
  totalUsage: number;
  /** Omitted when the ledger has not attributed prepaid usage yet */
  prepaidUsage?: number;
  payLaterUsage?: number;
  transactionCount?: number;
  periodStart?: string | null;
  periodEnd?: string | null;
}

import type {
  ContractCommitmentBillingDetails,
  CreditGrantBillingRow,
  SubscriptionInfo,
} from "@/utils/billing/types";

export type { ContractCommitmentBillingDetails, CreditGrantBillingRow };

export type CurrentPlan = SubscriptionInfo;

export interface BillingData {
  currentPlan: CurrentPlan;
}

export interface PricingTableData {
  "pricing-table-id": string;
  "publishable-key": string;
  "customer-session-client-secret": string;
}

export interface BillingInfoResponse {
  data: {
    content:
      | BillingData
      | {
          url?: string;
          "customer-session-client-secret"?: string;
          "pricing-table-id"?: string;
          "publishable-key"?: string;
        };
    status_code?: number;
  };
}

export type BillingActionType =
  "subscription_cancel" | "subscription_update" | undefined;

/** Cancellation reason options matching backend REASON_CHOICES */
export type CancellationReason =
  | "too_expensive"
  | "not_using"
  | "missing_features"
  | "switching_competitor"
  | "technical_issues"
  | "temporary_pause"
  | "business_closed"
  | "other";

/** Cancellation reason display labels */
export const CANCELLATION_REASONS: {
  value: CancellationReason;
  label: string;
}[] = [
  { value: "too_expensive", label: "Too expensive" },
  { value: "not_using", label: "Not using it enough" },
  { value: "missing_features", label: "Missing features I need" },
  { value: "switching_competitor", label: "Using another service" },
  { value: "technical_issues", label: "Technical issues" },
  { value: "temporary_pause", label: "Taking a temporary break" },
  { value: "business_closed", label: "Business closing/changing" },
  { value: "other", label: "Other" },
];

/** Cancellation preview response from backend */
export interface CancellationPreview {
  /** Whether within 14-day refund window */
  eligibleForRefund: boolean;
  /** Estimated refund in dollars (if eligible) */
  refundAmount: number;
  /** Credits consumed this period */
  creditsUsed: number;
  /** Total credits in plan */
  totalCredits: number;
  /** Equivalent days of usage based on credits */
  daysConsumed: number;
  /** Actual days since plan_start_date */
  daysSinceStart: number;
  /** When current period started (ISO date) */
  planStartDate: string | null;
  /** When access would end (ISO date) */
  effectiveDate: string;
  /** When credits would expire (ISO date) */
  creditsExpireDate: string;
  /** Current plan name */
  planName: string;
  /** Current plan price in dollars */
  planPrice: number;
  /** Whether this is a postpaid (metered/pay-by-refund) plan */
  isPostpaid: boolean;
}

/** Request body for cancellation */
export interface CancelSubscriptionRequest {
  /** Cancellation reason (required) */
  reason: CancellationReason;
  /** Optional additional feedback */
  additional_comments?: string;
}

/** Response from cancellation endpoint */
export interface CancelSubscriptionResponse {
  /** Whether cancelled immediately or at period end */
  cancelledImmediately: boolean;
  /** Refund amount in dollars (if applicable) */
  refundAmount?: number;
  /** Credits consumed */
  creditsUsed?: number;
  /** Equivalent days of usage */
  daysConsumed?: number;
  /** When access ends (ISO date) */
  effectiveDate: string;
  /** When credits expire (ISO date) */
  creditsExpireDate: string;
}

/** Bank approval status for Pay by Refund users */
export type BankApprovalStatus =
  | "approved"
  | "pending"
  | "needs_action"
  | "denied"
  | "not_supported"
  | "no_bank_product";

/** Bank product information for Pay by Prep Fees */
export interface PayByPrepFeesInfo {
  /** The bank product being used (e.g., "SBTPG", "EPS", "Refund Advantage") */
  bankProduct: string | null;
  /** Whether this bank is supported for Pay by Refund */
  isBankSupported: boolean;
  /** The approval status from the bank */
  approvalStatus: BankApprovalStatus | null;
  /** The EFIN associated with this approval */
  efin: string | null;
  /** Optional message from the bank about the approval */
  approvalMessage?: string;
  /** Date when approval status was last updated */
  lastUpdated?: string;
}

/** Request body for updating bank status */
export interface UpdateBankStatusRequest {
  /** New EFIN value (6 digits) */
  efin?: string;
  /** New bank product selection */
  bankProduct?: string;
}

/** Available bank product options for Pay by Prep Fees */
export const BANK_PRODUCT_OPTIONS = [
  { id: "SBTPG", name: "SBTPG", fullName: "Santa Barbara Tax Product Group" },
  {
    id: "Refund Advantage",
    name: "Refund Advantage",
    fullName: "Refund Advantage",
  },
  { id: "EPS", name: "EPS", fullName: "EPS" },
  { id: "Pay Dash", name: "Pay Dash", fullName: "Pay Dash" },
  { id: "Republic", name: "Republic", fullName: "Republic Bank" },
  { id: "Refundo", name: "Refundo", fullName: "Refundo" },
  { id: "N/A", name: "N/A", fullName: "I don't use bank products" },
] as const;

/** Banks that support Pay by Prep Fees */
export const SUPPORTED_PAY_BY_REFUND_BANKS = [
  "SBTPG",
  "Refund Advantage",
  "EPS",
  "Pay Dash",
] as const;
